import { posClient } from "@/lib/api/client";
import { readMockAccount } from "@/lib/data/mockAccount";
import type { Category, ChoiceGroup, Context, LineModifier, MenuItem, Order, Receipt } from "@/lib/api/types";
import {
  categoriesFixture,
  choiceGroupsFixture,
  menuFixture,
  orderFixture,
  receiptFixture,
  contextFixtureForMockRole,
} from "@/lib/data/fixtures";

/**
 * Data source for the first slice. Defaults to MOCK (fixtures) so pos-webapp renders and is
 * reviewable before pos-orchestrator exists. Set NEXT_PUBLIC_POS_LIVE=1 to hit the real BFF.
 *
 * Money is server-authoritative: in mock mode we do NOT recompute totals for mutating intents.
 * Those throw MockUnsupported so the UI can show an honest "connect the BFF" note instead of
 * faking a computed bill.
 */

export const LIVE = process.env.NEXT_PUBLIC_POS_LIVE === "1";

export class MockUnsupported extends Error {
  constructor() {
    super("Server-computed intent — connect pos-orchestrator (NEXT_PUBLIC_POS_LIVE=1) to run this.");
    this.name = "MockUnsupported";
  }
}

/**
 * Which counter the mock build plays: `?mockPack=desk` once, then remembered for this tab so the desk's
 * own links keep working. Mock builds only; a live counter's pack comes from its session.
 */
function mockPack(): string | null {
  if (typeof window === "undefined") return null;
  const asked = new URLSearchParams(window.location.search).get("mockPack");
  try {
    if (asked) window.sessionStorage.setItem("pos.mockPack", asked);
    return asked ?? window.sessionStorage.getItem("pos.mockPack");
  } catch {
    return asked;
  }
}

/** `?mockAccount=on-hold` once, then remembered for this tab: the mock build plays an account on hold. */
function mockAccountOnHold(): boolean {
  if (typeof window === "undefined") return false;
  const asked = new URLSearchParams(window.location.search).get("mockAccount");
  try {
    if (asked) window.sessionStorage.setItem("pos.mockAccount", asked);
    return readMockAccount(asked, window.sessionStorage.getItem("pos.mockAccount")) === "ON_HOLD";
  } catch {
    return readMockAccount(asked, null) === "ON_HOLD";
  }
}

export const dataSource = {
  live: LIVE,

  getContext: (): Promise<Context> => {
    if (LIVE) return posClient.getContext();
    const role = typeof window === "undefined" ? null : new URLSearchParams(window.location.search).get("mockRole");
    const context = contextFixtureForMockRole(role, mockPack());
    return Promise.resolve(mockAccountOnHold() ? { ...context, accountState: "ON_HOLD" } : context);
  },

  getMenu: (): Promise<{ categories?: Category[]; items: MenuItem[] }> =>
    LIVE ? posClient.getMenu() : Promise.resolve({ categories: categoriesFixture, items: menuFixture }),

  getChoices: (itemId: string): Promise<{ groups: ChoiceGroup[] }> =>
    LIVE ? posClient.getChoices(itemId) : Promise.resolve({ groups: choiceGroupsFixture[itemId] ?? [] }),

  // Live: open a fresh order for this counter session (the fixture id doesn't exist server-side).
  getOrder: (): Promise<Order> =>
    LIVE
      ? posClient.openOrder({ orderType: "DINE_IN", tableId: "T-07", guests: 2 })
      : Promise.resolve(orderFixture),

  addLine: (orderId: string, itemId: string, modifiers?: LineModifier[], note?: string): Promise<Order> =>
    LIVE ? posClient.addLine(orderId, { itemId, qty: 1, modifiers, note }) : Promise.reject(new MockUnsupported()),

  applyPromo: (orderId: string, code: string): Promise<Order> =>
    LIVE ? posClient.applyPromo(orderId, code) : Promise.reject(new MockUnsupported()),

  addTender: (orderId: string, method: Parameters<typeof posClient.addTender>[1]["method"], maxOut = true): Promise<Order> =>
    LIVE ? posClient.addTender(orderId, { method, maxOut }) : Promise.reject(new MockUnsupported()),

  confirm: (orderId: string): Promise<Receipt> =>
    LIVE ? posClient.confirm(orderId) : Promise.resolve(receiptFixture),
};
